/**
 * The rundown editor's top bar: the rundown's name and totals, the create
 * buttons (each enabled where the focused row gives it a place), undo and
 * redo, delete, the save and on-air protection state (`status`), and who
 * else is here. Presentational: every button is an event.
 */
import { PlusIcon, Redo2Icon, TrashIcon, Undo2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { formatDuration } from '../../lib/editor/duration.js';
import type { TreeKind } from '../../lib/editor/viewModel.js';
import { Button } from '../ui/button.js';

export type EditorToolbarProps = {
  readonly title: string;
  readonly totalDuration: number;
  readonly canCreate: Readonly<Record<TreeKind, boolean>>;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly canDelete: boolean;
  readonly onCreate: (kind: TreeKind) => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly onDelete: () => void;
  /** The save state and the on-air protection toggle. */
  readonly status: ReactNode;
  /** The presence avatars. */
  readonly presence: ReactNode;
};

export function EditorToolbar({ title, totalDuration, canCreate, canUndo, canRedo, canDelete, onCreate, onUndo, onRedo, onDelete, status, presence }: EditorToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-card px-4 py-2" data-testid="editor-toolbar">
      <div className="mr-2 min-w-0">
        <h1 className="truncate text-lg font-semibold" data-testid="editor-title">
          {title}
        </h1>
        <div className="font-mono text-xs text-muted-foreground" data-testid="rundown-total">
          {formatDuration(totalDuration)} total
        </div>
      </div>
      <div className="flex items-center gap-1" role="group" aria-label="Create">
        <Button size="sm" variant="outline" onClick={() => onCreate('segment')} disabled={!canCreate.segment} data-testid="new-segment">
          <PlusIcon /> Segment
        </Button>
        <Button size="sm" variant="outline" onClick={() => onCreate('part')} disabled={!canCreate.part} data-testid="new-part">
          <PlusIcon /> Part
        </Button>
        <Button size="sm" variant="outline" onClick={() => onCreate('piece')} disabled={!canCreate.piece} data-testid="new-piece">
          <PlusIcon /> Piece
        </Button>
        <Button size="sm" variant="outline" onClick={() => onCreate('adlib')} disabled={!canCreate.adlib} data-testid="new-adlib">
          <PlusIcon /> Adlib
        </Button>
      </div>
      <div className="flex items-center gap-1" role="group" aria-label="History">
        <Button size="sm" variant="ghost" onClick={onUndo} disabled={!canUndo} aria-label="Undo" title="Undo (Ctrl+Z)" data-testid="undo">
          <Undo2Icon />
        </Button>
        <Button size="sm" variant="ghost" onClick={onRedo} disabled={!canRedo} aria-label="Redo" title="Redo (Ctrl+Shift+Z)" data-testid="redo">
          <Redo2Icon />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} disabled={!canDelete} aria-label="Delete" title="Delete (Del)" data-testid="delete-rows">
          <TrashIcon />
        </Button>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {status}
        {presence}
      </div>
    </div>
  );
}
